/**
 * Shim between the global trackEvent() fan-out (lib/analytics.ts) and the
 * async-loaded posthog-js instance. It holds the client in a module-level ref set
 * by components/posthog-analytics.tsx once the SDK loads — so this module (pulled
 * into many client bundles via lib/analytics.ts) never statically imports the
 * ~entire posthog-js bundle; only the type is imported.
 *
 * Because the SDK loads via async dynamic import, events fired during initial page
 * load (e.g. "Experiment Enrolled", "Billing Viewed") would otherwise be dropped
 * before the client exists. They're buffered and flushed once the client is set —
 * but only when analytics consent was granted at enqueue time AND still holds at
 * flush time, so a pre-consent or revoked-mid-load event never reaches PostHog.
 *
 * The module-level refs below are reassigned (never mutated in place — the buffer
 * is rebuilt with spreads) as the client initializes and drains. A mutable
 * singleton is the unavoidable shape for "set once the SDK finishes loading"; it
 * is intentionally exempt from the functional-style rule for the same reason
 * plausible-analytics.tsx keeps a module-level init flag.
 */
import { stripNullish } from "./analytics-props";
import { readStoredConsent } from "./consent-state";

import type { PostHog } from "posthog-js";

type QueuedEvent = readonly [string, Record<string, string | number | boolean>];

// Bound the buffer so it can't grow without limit; only the most recent are kept.
const MAX_PENDING = 50;

type Identity = {
  id: string;
  props: Record<string, string | number | boolean>;
  // Internal team account — keep events (filtered from insights server-side) but
  // never capture session replay (replay isn't governed by the test-account cohort).
  internal: boolean;
};

let client: PostHog | null = null;
// Whether the CURRENT page is one replay may record (anonymous + marketing
// allowlist, decided by components/posthog-replay-gate.tsx). Held here for the
// same reason `identity` is: the decision can be made before the SDK finishes
// loading or before consent lands, and must be re-applied when either arrives.
let replayAllowed = false;
let pending: readonly QueuedEvent[] = [];
// The desired identity, set by identifyPosthogUser (from ProfileMenu — the shared
// authed component). Held until BOTH the client is ready AND analytics consent
// holds, then applied — mirroring the pending-event buffer, so a login that
// precedes the async SDK load or a consent grant isn't lost. Reassigned, never
// mutated.
let identity: Identity | null = null;
// The id we've actually called identify() with, so re-mounts of ProfileMenu (it
// renders on every authed surface, sometimes twice per page) collapse to one
// identify call. Cleared on reset so a later re-login re-identifies.
let identifiedId: string | null = null;

/** Called once by the analytics component after posthog-js loads and inits. */
export const setPosthogClient = (instance: PostHog): void => {
  client = instance;
  // The gate almost always decides before the SDK finishes loading; re-apply so
  // a marketing visit that started pre-load still records from here on.
  applyReplay();
};

/**
 * Links the current (anonymous) distinct_id to the logged-in user so prior and
 * future events fold into one person. No-op until the client is ready and
 * analytics consent is granted; applyIdentity re-runs from setPosthogConsent so a
 * same-session login→consent still identifies. distinct_id is the stable internal
 * user id — email and name are deliberately NOT sent as person properties (see
 * components/posthog-identity.tsx); the id joins back to our own tables when a
 * question needs a person.
 */
export const identifyPosthogUser = (
  id: string,
  props?: Record<string, string | number | boolean | null | undefined>,
  options?: { internal?: boolean },
): void => {
  identity = { id, props: stripNullish(props), internal: options?.internal ?? false };
  applyIdentity();
};

/**
 * Clears the identity and resets posthog's distinct_id (new anonymous id, stored
 * person props dropped) — called before logout so the next user on a shared device
 * doesn't inherit the previous person.
 */
export const resetPosthogUser = (): void => {
  identity = null;
  identifiedId = null;
  client?.reset();
};

const applyIdentity = (): void => {
  if (!client || !identity) return;
  if (readStoredConsent()?.analytics !== true) return;
  if (identity.id !== identifiedId) {
    client.identify(identity.id, identity.props);
    identifiedId = identity.id;
  }
  // Belt and braces. Replay is disabled outright at init
  // (components/posthog-analytics.tsx), so nothing is recording and this is a
  // no-op today. It stays because it costs nothing and because if replay is ever
  // re-enabled for a narrower audience, insiders should still be excluded
  // without anyone having to remember to re-add this line.
  if (identity.internal) client.stopSessionRecording();
};

/**
 * Start or stop the recorder for the current page.
 *
 * The SDK is initialised with `disable_session_recording: true`, so nothing ever
 * records on its own — recording exists only for as long as this function has
 * been told it may. That is the point: PostHog's project-level "Record user
 * sessions" toggle has to be ON for a recorder config to be served at all, but
 * with it on, this is what decides. Flipping the project setting cannot by
 * itself resume recording the signed-in app.
 *
 * Called on every client-side navigation, so leaving the allowlist stops the
 * recorder mid-session rather than waiting for a page load.
 */
const applyReplay = (): void => {
  if (!client) return;
  if (replayAllowed && readStoredConsent()?.analytics === true) client.startSessionRecording();
  else client.stopSessionRecording();
};

/** Told by the replay gate whether the current page may be recorded. Re-applied
 *  when the client arrives or consent changes. */
export const setReplayAllowed = (allowed: boolean): void => {
  replayAllowed = allowed;
  applyReplay();
};

/**
 * Flushes events buffered before the client was ready. Re-checks consent at flush
 * time: if it was revoked while the SDK loaded, the buffer is dropped rather than
 * trusting the SDK's opt-out backstop alone.
 */
export const flushPosthogQueue = (): void => {
  if (!client || pending.length === 0) return;
  if (readStoredConsent()?.analytics !== true) {
    pending = [];
    return;
  }
  const queued = pending;
  pending = [];
  queued.forEach(([eventName, props]) => client?.capture(eventName, props));
};

/**
 * Routes a trackEvent() call to PostHog. Human-readable names (e.g. "Agent Hired")
 * are kept as-is for cross-tool consistency with Plausible; PostHog preserves
 * native prop types, so only null/undefined are stripped. Before the client is
 * ready the event is buffered, but only if analytics consent already exists —
 * pre-consent events must never reach PostHog.
 */
export const trackPosthogEvent = (
  eventName: string,
  props?: Record<string, string | number | boolean | null | undefined>,
): void => {
  if (typeof window === "undefined") return;

  const cleanProps = stripNullish(props);

  if (client) {
    client.capture(eventName, cleanProps);
    return;
  }

  if (readStoredConsent()?.analytics !== true) return;
  pending = [...pending, [eventName, cleanProps] as const].slice(-MAX_PENDING);
};

/**
 * Bridges a *runtime* sq_consent change into PostHog (called from lib/consent.ts
 * when the user accepts/rejects in the banner). The initial page-load opt state is
 * applied in the component's loaded callback, so this handles live transitions.
 *
 * Granting flips persistence to the durable store and opts in *silently*
 * (captureEventName: null suppresses PostHog's default $opt_in event — sq_consent
 * is our consent record). opt_in_capturing itself emits the initial pageview that
 * was deferred while opted out, so we must NOT capture one manually here or the
 * first pageview doubles. Withdrawing opts out and drops back to in-memory
 * persistence so no cookies linger.
 */
export const setPosthogConsent = (granted: boolean): void => {
  if (!client) return;

  if (granted) {
    client.set_config({ persistence: "localStorage+cookie" });
    client.opt_in_capturing({ captureEventName: null });
    // A login that landed before consent was granted left a pending identity;
    // apply it now that capturing is on.
    applyIdentity();
  } else {
    client.opt_out_capturing();
    client.set_config({ persistence: "memory" });
  }
  // Consent gates replay as strictly as it gates events: granting it may start a
  // recorder on an allowlisted page, revoking it must stop one immediately.
  applyReplay();
};
